// Game balance: building/unit definitions and simulation constants.
// Shared. FROZEN CONTRACT (values may be tuned, shapes may not).

import { Terrain } from './terrain'
import type { BuildingType, ItemKind, ItemType, Resource, SkillKey, Stockpile, UnitType } from './types'

// ---- Simulation pacing --------------------------------------------------------

export const TICK_MS = 1000 // one simulation tick per second (slow-paced)
export const DAY_TICKS = 600 // full day/night cycle every 10 minutes
export const TREE_GROW_TICKS = 3 * DAY_TICKS // planted saplings mature after three full game days
export const WILDLIFE_GRACE_TICKS = 2 * DAY_TICKS // two full days after founding before animals can attack
export const SAVE_INTERVAL_TICKS = 60

// Population & food
export const GROWTH_TICKS = 45 // a new serf every 45s per town hall…
export const GROWTH_FOOD_MIN = 0 // …while any food remains in storage and housing is free

// Per-serf hunger. Each serf carries a 0..100 food meter that drains over time;
// they eat carried food (or from the town-hall stores when near) once peckish.
export const FOOD_METER_MAX = 100
export const FOOD_DECAY_PER_TICK = 0.16 // meter points lost/tick — a full meter lasts ~1 game day
export const FOOD_EAT_BELOW = 60 // a serf looks for a bite once their meter drops under this
export const FOOD_PER_MEAL = 5 // each food eaten restores this many meter points
export const FOOD_HEAL_FRAC = 0.01 // each meal also mends this fraction of max HP
export const FOOD_CARRY = 6 // food units a serf tops up to when at the granary
export const FOOD_WEIGHT = 0.5 // weight of one carried food (nudges Speed down a touch)
export const FOOD_RESTOCK_RANGE = 3 // hexes from a town hall/storehouse to eat from & restock
/** Game hours a serf may sit on an empty meter before it costs them health. DAY_TICKS = 24h. */
export const FOOD_EMPTY_TICKS = DAY_TICKS // 24 game hours
export const FOOD_STARVE_HP_FRAC = 0.1 // max-HP fraction lost per 24h of starvation

// Logistics
export const CARRIER_PICKUP_MIN = 8 // carriers wait until a building buffered this much
export const CARRIER_CAPACITY = 20
export const CARRIERS_PER_HUB = 2 // free carriers maintained per townhall/storehouse
export const BUFFER_CAP = 60 // production stalls when a building's buffer is full
/** Pathfinding node budget for haul trips. The default 600-node budget breaks
 *  hauling at ~27 hexes straight-line on open grass (less on rough terrain), so
 *  faraway camps silently never get a carrier. Hauls get a raid-sized budget. */
export const HAUL_PATH_NODES = 4000

// Territory
export const TERRITORY_R = 2 // hexes beyond each tile of a completed building's footprint
export const TERRITORY_R_TOWNHALL = TERRITORY_R + 1 // center radius of the seven-hex hall plus its border
export const BUILD_REACH = 1 // may build on own territory or within 1 hex of it

// Spawning
export const PLAYER_SPAWN_MIN_DIST = 40 // min hexes between player town halls
export const SPAWN_SAFE_RADIUS = 18 // no hostile NPCs within this of a fresh spawn
export const DEMOLISH_REFUND = 0.25
export const RESPAWN_KEEP = 0.5 // fraction of stockpile kept when respawning after ruin

// Building decay + repair. A finished building weathers BUILDING_DECAY_PER_DAY
// of its max HP per game day (spread across passes every BUILDING_DECAY_EVERY
// ticks). Idle builders (hammer in hand) auto-repair their realm's damaged
// buildings back to full at construction speed, spending the *same* fraction of
// the build cost as the HP they restore — so a realm with folk + resources
// stays maintained, while a neglected or resource-starved one decays until its
// buildings are destroyed. An *abandoned* realm never repairs and decays
// ABANDONED_DECAY_MULT× faster, so a village left behind crumbles to rubble.
export const BUILDING_DECAY_PER_DAY = 0.05
export const BUILDING_DECAY_EVERY = 60
export const ABANDONED_DECAY_MULT = 5

// Upkeep: instead of weathering unconditionally, each finished player building
// pays a small daily materials bill (a share of its build cost) from its own
// maintenance store (Building.mstore, hauler-topped-up). A building whose store
// can't cover the bill takes the day's decay damage for the missing share.
export const UPKEEP_COST_FRAC = 0.05 // share of the build cost owed per game day
export const UPKEEP_STORE_DAYS = 2 // maintenance store cap, in days of upkeep

/** Spoil from one Dig order: dirt (or sand, dug from sand), carted to storage. */
export const DIG_YIELD = 10
/** Fill can't stack land higher than this tier (matches the render clamp). */
export const FILL_MAX_TIER = 8
/** Terraform orders: shovel-only work, never normal construction. */
export function isTerraform(t: BuildingType): boolean {
  return t === 'dig' || t === 'fill' || t === 'fillsand'
}
/** The fill orders (either material). */
export function isFill(t: BuildingType): boolean {
  return t === 'fill' || t === 'fillsand'
}
/** Orders you lay by press-and-drag across many hexes — roads, the single-hex
 *  terraform orders (dig / fill / fillsand) and chop-tree orders. Structures
 *  place one at a time. */
export function isPaintable(t: BuildingType): boolean {
  return t === 'road' || t === 'chop' || t === 'plant' || isTerraform(t)
}
/** Orders laid by dragging a rubber-band BOX over an area (chop and the terraform
 *  orders): every matching hex inside the box is selected at once, a status badge
 *  floats over each queued hex, and a Remove sub-mode cancels them. Roads keep the
 *  press-and-drag PATH stroke (isPaintable but not isBoxOrder). */
export function isBoxOrder(t: BuildingType): boolean {
  return t === 'chop' || t === 'plant' || isTerraform(t)
}

// Farms: the land itself sets the yield — each grass hex under the fields gives
// 1 food/min, each fertile hex 1.5 (at nominal full staffing: two bare-handed
// level-5 workers; hoes double each farmhand's output).
export const FARM_FOOD_PER_GRASS = 1
export const FARM_FOOD_PER_FERTILE = 1.5
/** The nominal food/min the farm's produces-rate encodes (2 workers × 1/min),
 *  which the per-hex rates are expressed against. */
const FARM_NOMINAL_PER_MIN = 2

/** Food/min a farm on these footprint hexes yields at nominal full staffing. */
export function farmHexRate(terrains: (Terrain | undefined)[]): number {
  let f = 0
  for (const t of terrains) f += t === Terrain.Fertile ? FARM_FOOD_PER_FERTILE : FARM_FOOD_PER_GRASS
  return f
}

/** Multiplier applied to the farm's produces rate so worker power still scales:
 *  all-grass = 1.5 (3/min), all-fertile = 2.25 (4.5/min). */
export function farmYieldMult(terrains: (Terrain | undefined)[]): number {
  return farmHexRate(terrains) / FARM_NOMINAL_PER_MIN
}

/** The daily maintenance bill for a building type: one small whole-unit charge
 *  per material in its build cost. Roads (1 HP infra) owe nothing. */
export function upkeepDaily(type: BuildingType): Partial<Stockpile> {
  const out: Partial<Stockpile> = {}
  if (type === 'road' || type === 'plant') return out
  const cost = BUILDINGS[type].cost
  for (const res in cost) {
    const c = cost[res as Resource]
    if (c) out[res as Resource] = Math.max(1, Math.round(c * UPKEEP_COST_FRAC))
  }
  return out
}
/** Builders are dispatched to repair a building once its HP drops below this fraction. */
export const REPAIR_ENGAGE = 0.9

// Starting kit for a new (or respawned) player village
export const START_STOCKPILE: Stockpile = { food: 30, wood: 150, stone: 60, iron: 0, gold: 10, dirt: 0, sand: 0 }
/** Two empty-handed founders; the first hammer is crafted at the hall bench. */
export const START_SERFS = 2
/** The campsite has spare room for the village to grow beyond its two founders. */
export const START_HOUSING = 5
export const BASE_CAPACITY = 500 // storage provided by the town hall itself
/** Work pace without the proper tool in hand (serfs can always pitch in slowly). */
export const BARE_HAND_MULT = 0.25
/** Gathering pace with the RIGHT tool in hand — a 25% bonus over the plain rate.
 *  (Farms and piers reward their tool separately: a hoe doubles a farmhand, a
 *  rod is required to fish at all.) */
export const TOOL_GATHER_MULT = 1.25
/** Armed serfs auto-engage hostiles and defend friends within this many hexes. */
export const GUARD_RANGE = 6
/** Serfs within this range of an attacked friend react at once: fight-stance folk
 *  pile in (bare fists included), flee-stance folk bolt for home. */
export const HELP_RANGE = 4
/** Training dummies teach a weapon skill up to this level; real combat beyond. */
export const DUMMY_SKILL_CAP = 6

// ---- Footprints -----------------------------------------------------------------
// Most buildings occupy a single hex; the town hall sprawls across a 7-hex rosette —
// its anchor plus all six surrounding hexes (a full hexagon of grounds). Offsets are
// axial (dq,dr): the centre plus the six neighbour directions.

export const TOWNHALL_FOOTPRINT: readonly (readonly [number, number])[] = [
  [0, 0],
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
]

/** The farm's fields: a 3-hex triangle — the anchor plus two adjacent neighbours.
 *  `dir` (0..5, R-rotation while placing) spins the triangle around the anchor in
 *  60° steps; dir 0 is the anchor's east + south-east pair. */
export const FARM_FOOTPRINT: readonly (readonly [number, number])[] = [
  [0, 0],
  [1, 0],
  [0, 1],
]

/** The hexes a building of `type` planted at anchor (q,r) physically occupies. */
/** Axial neighbor offsets, DIRS order (shared with hex.ts). */
const FOOT_DIRS: [number, number][] = [
  [1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1],
]

export function buildingFootprint(
  type: BuildingType,
  q: number,
  r: number,
  dir?: number,
): { q: number; r: number }[] {
  if (type === 'townhall') return TOWNHALL_FOOTPRINT.map(([dq, dr]) => ({ q: q + dq, r: r + dr }))
  if (type === 'farm') {
    const p = (((dir ?? 0) % 6) + 6 + 5) % 6 // dir 0 ≡ the FOOT_DIRS (5,0) pair: east + south-east
    const a = FOOT_DIRS[p]
    const b = FOOT_DIRS[(p + 1) % 6]
    return [{ q, r }, { q: q + a[0], r: r + a[1] }, { q: q + b[0], r: r + b[1] }]
  }
  if (type === 'pier') {
    const d = FOOT_DIRS[((dir ?? 0) % 6 + 6) % 6]
    return [
      { q, r }, // the land anchor
      { q: q + d[0], r: r + d[1] }, // the deck, out over the water
    ]
  }
  return [{ q, r }]
}

// ---- Buildings ------------------------------------------------------------------

export interface BuildingDef {
  name: string
  desc: string
  cost: Partial<Stockpile>
  /** Builder-ticks to complete (one builder present = 1/tick). */
  buildTime: number
  hp: number
  /** Housing (population cap) provided. */
  housing?: number
  /** Storage capacity added per resource. */
  storage?: number
  /** Units cannot enter this building's hex (walls). */
  blocks?: boolean
  /** Worker slots for production. */
  workerSlots?: number
  /** Output per worker per tick. */
  produces?: Partial<Record<Resource, number>>
  /** Terrain the building itself may stand on (undefined = any buildable terrain). */
  on?: Terrain[]
  /** Requires at least one neighboring hex of one of these terrains. */
  adjacent?: Terrain[]
  /** Static defense. period = ticks between shots. */
  attack?: { dmg: number; range: number; period: number }
  /** Unit types trainable here (NPC/legacy). */
  trains?: UnitType[]
  /** Gear items this building can forge into the town-hall armory (Smithy). */
  forges?: ItemType[]
}

export const BUILDINGS: Record<BuildingType, BuildingDef> = {
  townhall: {
    name: 'Town Hall',
    desc: 'Heart of your village, beginning as a small campsite on a seven-hex plot. Stores goods, houses folk, and holds the armory where serfs swap their gear. New serfs are born here as food and housing allow. Any serf can whittle the simple wooden tools at its bench — order them below.',
    cost: { wood: 120, stone: 60 },
    buildTime: 60,
    hp: 1200,
    housing: 10,
    storage: BASE_CAPACITY,
    attack: { dmg: 4, range: 2, period: 2 },
    // Wooden gear only (wood-cost, no stone or metal): the hall is the village's
    // fallback toolmaker, so basic labor is never locked behind another building.
    // The spear is wood-tier too — a fire-hardened point, whittled like the rest.
    forges: ['hammer', 'shovel', 'hoe', 'axe', 'pick', 'rod', 'spear'],
  },
  house: {
    name: 'House',
    desc: '+5 population cap.',
    cost: { wood: 30 },
    buildTime: 25,
    hp: 200,
    housing: 5,
  },
  farm: {
    name: 'Farm',
    desc: 'Three hexes of tilled fields: each grass hex yields 1 food/min, each fertile hex 1.5 with two level-5 farmers. Hoes double output. Beginners produce 20%; skills above 5 add 5% per level.',
    cost: { wood: 20 },
    buildTime: 25,
    hp: 150,
    workerSlots: 2,
    produces: { food: 1 / 60 }, // 1/min per worker; hoe doubles it; ×terrain
    on: [Terrain.Grass, Terrain.Fertile],
  },
  chop: {
    name: 'Chop Trees',
    desc: 'Mark trees to be felled — drag to select a stand of them. Only serfs with an axe chop, roughly a second per wood: small trees give 10, the greatest 100. The wood drops where the tree stood for haulers to cart to storage.',
    cost: {},
    buildTime: 40, // nominal only — the real felling time is the tree's wood (siteBuildTime)
    hp: 1, // an order, not a structure — no health to speak of
    // No `on`: a chop targets a standing TREE (a per-tile flag over grass), not a terrain
    // type — canPlace/ghostValid gate it on tree presence instead. See hasTreeAt / treeAt.
  },
  plant: {
    name: 'Plant Trees',
    desc: 'Mark empty grass or fertile land for planting. Each hex costs 1 food, delivered by villagers before they plant by hand. Trees become harvestable after 3 game days. No tools required.',
    cost: { food: 1 },
    buildTime: 10,
    hp: 1,
    on: [Terrain.Grass, Terrain.Fertile],
  },
  quarry: {
    name: 'Quarry',
    desc: 'Cuts stone. Build on hills or beside mountains.',
    cost: { wood: 25 },
    buildTime: 30,
    hp: 200,
    workerSlots: 3,
    produces: { stone: 1 / 60 }, // 3/min fully staffed (3)
    on: [Terrain.Hills, Terrain.Grass, Terrain.Fertile, Terrain.Sand, Terrain.Snow],
    adjacent: [Terrain.Mountain, Terrain.Hills],
  },
  mine: {
    name: 'Mine',
    desc: 'Digs iron and a trickle of gold. Must border mountains.',
    cost: { wood: 40, stone: 20 },
    buildTime: 40,
    hp: 250,
    workerSlots: 6,
    produces: { iron: 1 / 180, gold: 1 / 900 }, // 1/min iron + 0.2/min gold fully staffed
    adjacent: [Terrain.Mountain],
  },
  storehouse: {
    name: 'Storehouse',
    desc: '+200 storage and two carriers. Place near production to shorten hauls.',
    cost: { wood: 50 },
    buildTime: 35,
    hp: 300,
    storage: 200,
  },
  workbench: {
    name: 'Workbench',
    desc: 'Hand-crafts wood-tier tools and weapons while a serf works it. The first step of every craft chain.',
    cost: { wood: 20 },
    buildTime: 20,
    hp: 200,
    workerSlots: 1,
    // (No bow: it is out of the game for now — see the 2026-07-14 changelog.)
    forges: ['hammer', 'hoe', 'spear', 'rod', 'axe', 'pick', 'shovel', 'smithhammer'],
  },
  // Kept the type key `barracks` for save-compatibility; it is now the Smithy.
  barracks: {
    name: 'Smithy',
    desc: "Forges metal-tier gear — but only while a crafter wielding a smith's hammer works the anvil.",
    cost: { wood: 60, stone: 40 },
    buildTime: 45,
    hp: 400,
    workerSlots: 1,
    forges: ['sword'],
  },
  pier: {
    name: 'Fishing Pier',
    desc: 'A wooden deck over the shallows. Two serfs with fishing rods land 3 food/min each — rods only, no bare hands.',
    cost: { wood: 25 },
    buildTime: 20,
    hp: 200,
    workerSlots: 2,
    produces: { food: 1 / 40 }, // 3/min per equipped fisher: shoreline food without fertile land
    on: [Terrain.Grass, Terrain.Sand, Terrain.Snow, Terrain.Fertile],
  },
  palisade: {
    name: 'Palisade',
    desc: 'A wooden stake wall. Blocks everyone on foot — raiders must batter it down or walk around. Leave a gap for your own folk.',
    cost: { wood: 8 },
    buildTime: 8,
    hp: 400,
    blocks: true,
  },
  dummy: {
    name: 'Training Dummy',
    desc: 'Idle soldiers nearby wander over and spar of their own accord, sharpening their weapon skill up to level 6 — real fighting teaches the rest.',
    cost: { wood: 15 },
    buildTime: 15,
    hp: 150,
  },
  tower: {
    name: 'Watchtower',
    desc: 'Shoots arrows at hostiles.',
    cost: { wood: 30, stone: 60 },
    buildTime: 40,
    hp: 500,
    attack: { dmg: 8, range: 3, period: 2 },
  },
  ramp: {
    name: 'Ramp',
    desc: 'A built incline up a cliff. Bridges one otherwise-impassable step of high ground so your folk can climb it. Rotate with R to face the cliff.',
    cost: { wood: 20, stone: 10 },
    buildTime: 18,
    hp: 200,
  },
  road: {
    name: 'Road',
    desc: 'Halves movement cost. Logistics win kingdoms.',
    cost: { stone: 4 },
    buildTime: 6,
    hp: 1,
  },
  dig: {
    name: 'Dig',
    desc: 'A serf with a shovel (shovel required) lowers this hex one height step and carts off 10 dirt — 10 sand from sand — to storage. Grass, fertile plains or sand only.',
    cost: {},
    buildTime: 30,
    hp: 1, // an order, not a structure — no health to speak of
    on: [Terrain.Grass, Terrain.Fertile, Terrain.Sand],
  },
  fill: {
    name: 'Fill · Dirt',
    desc: 'Haulers bring 10 dirt; a serf with a shovel packs it down to raise this hex one height step — the ground greens over into grassland.',
    cost: { dirt: 10 },
    buildTime: 30,
    hp: 1, // an order, not a structure — no health to speak of
    on: [Terrain.Grass, Terrain.Fertile, Terrain.Sand],
  },
  fillsand: {
    name: 'Fill · Sand',
    desc: 'Haulers bring 10 sand; a serf with a shovel packs it down to raise this hex one height step — and the ground becomes sand.',
    cost: { sand: 10 },
    buildTime: 30,
    hp: 1, // an order, not a structure — no health to speak of
    on: [Terrain.Grass, Terrain.Fertile, Terrain.Sand],
  },
  camp: {
    name: 'Raider Camp',
    desc: 'A den of raiders. Destroy it for gold.',
    cost: {},
    buildTime: 1,
    hp: 600,
  },
}

/** The wood a forest tile yields when chopped down — a deterministic function of
 *  its hex, so server and client agree on tree size with no wire field. Skewed
 *  toward smaller trees; quantized to 10 (a sapling) … 100 (an ancient giant). */
export function treeWood(q: number, r: number): number {
  let h = (Math.imul(q, 374761393) + Math.imul(r, 668265263)) >>> 0
  h = (h ^ (h >>> 13)) >>> 0
  h = Math.imul(h, 1274126177) >>> 0
  const t = ((h >>> 8) & 0xffff) / 0x10000 // 0..1
  const size = Math.min(9, Math.floor(t * t * 10)) // 0..9, biased small
  return 10 + size * 10 // 10..100
}

/** Effective work time for a site (ticks). A chop order runs ~1s per wood — so a
 *  bigger tree takes longer to fell — while everything else uses its def.buildTime. */
export function siteBuildTime(type: BuildingType, q: number, r: number): number {
  return type === 'chop' ? treeWood(q, r) : BUILDINGS[type].buildTime
}

// ---- Units -------------------------------------------------------------------------

export interface UnitDef {
  name: string
  /** One-line description of what this class does differently (shown in the UI). */
  role: string
  cost?: Partial<Stockpile>
  trainTime?: number
  hp: number
  atk: number
  /** Attack range in hexes (1 = melee). */
  range: number
  /** Ticks between attacks. */
  period: number
  /** Base speed in hexes per tick (divided by terrain moveCost). */
  speed: number
  /** Auto-acquire hostiles within this range. 0 = never. */
  aggro: number
  /** Carrier capacity. */
  capacity?: number
  /** Food credited to the killer's faction when this dies (hunting). */
  foodDrop?: number
}

export const UNITS: Record<UnitType, UnitDef> = {
  citizen: { name: 'Serf', role: 'Your one and only kind of folk. Skills, not class, decide what they do — hand them a tool to work or a weapon to fight. Swap gear at the town-hall armory.', hp: 45, atk: 2, range: 1, period: 1, speed: 0.34, aggro: 0 },
  worker: { name: 'Worker', role: 'Builds, farms, mines and gathers. Assign them to work sites — unarmed, so keep them safe.', cost: { food: 20 }, trainTime: 15, hp: 25, atk: 1, range: 1, period: 2, speed: 0.34, aggro: 0 },
  carrier: { name: 'Carrier', role: 'Hauls produced goods from buildings back to storage. Spawned free by town halls and storehouses.', hp: 20, atk: 0, range: 1, period: 2, speed: 0.4, aggro: 0, capacity: CARRIER_CAPACITY },
  spearman: { name: 'Spearman', role: 'Sturdy melee soldier. High HP — your frontline against raiders and rival players.', cost: { food: 30, wood: 10, iron: 5 }, trainTime: 30, hp: 60, atk: 6, range: 1, period: 1, speed: 0.34, aggro: 2 },
  archer: { name: 'Archer', role: 'Ranged attacker (2 hexes) — strikes before melee lands, but fragile. Screen them with spearmen.', cost: { food: 25, wood: 20, iron: 5 }, trainTime: 35, hp: 40, atk: 5, range: 2, period: 1, speed: 0.34, aggro: 3 },
  raider: { name: 'Raider', role: 'Hostile marauder from camps and warlord keeps. Fast and aggressive — it will hunt your villagers.', hp: 50, atk: 7, range: 1, period: 1, speed: 0.4, aggro: 4 },
  wolf: { name: 'Wolf', role: 'Wild predator that hunts in the forests. Attacks lone workers; leaves 1 food when killed.', hp: 35, atk: 5, range: 1, period: 1, speed: 0.5, aggro: 3, foodDrop: 1 },
  bear: { name: 'Bear', role: 'Powerful beast of the mountains. Tough and hard-hitting; leaves 5 food when slain.', hp: 90, atk: 10, range: 1, period: 1, speed: 0.35, aggro: 2, foodDrop: 5 },
  deer: { name: 'Deer', role: 'Harmless game. Flees from your units — hunt it down for 10 food.', hp: 15, atk: 0, range: 1, period: 1, speed: 0.5, aggro: 0, foodDrop: 10 },
}

// ---- Items -----------------------------------------------------------------------------
// Real, finite objects a person can hold. Tools let them do a job; weapons let them fight.
// With no item in hand a person resorts to bare fists (never an item itself).

export interface ItemDef {
  name: string
  kind: ItemKind
  /** Skill this item exercises (build/farm/log/mine for tools; spear/sword/bow for weapons). */
  skill: SkillKey
  /** Weapons only: base attack and reach. */
  atk?: number
  range?: number
  /** Cost to forge one at a Smithy (into the town-hall armory). */
  forgeCost: Partial<Stockpile>
  /** Builder-ticks to forge one at a Smithy. */
  forgeTime: number
  /** One player-facing line: what a serf DOES with this in hand (craft hover). */
  desc: string
}

// Item tiers: WOOD and STONE tiers are hand-crafted at the Workbench; the
// METAL tier needs the Smithy and a crafter wielding a smith's hammer.
export const ITEMS: Record<ItemType, ItemDef> = {
  // -- wood tier (Workbench) --
  hammer: { name: 'Hammer', kind: 'tool', skill: 'build', forgeCost: { wood: 10 }, forgeTime: 15,
    desc: 'The builder’s tool: raises buildings from their blueprints and repairs worn ones. Nothing gets built without a hammer on site.' },
  hoe: { name: 'Hoe', kind: 'tool', skill: 'farm', forgeCost: { wood: 10 }, forgeTime: 15,
    desc: 'Works the farm fields — a serf with a hoe tends crops and brings in the harvest.' },
  spear: { name: 'Spear', kind: 'weapon', skill: 'spear', atk: 6, range: 1, forgeCost: { wood: 14 }, forgeTime: 22,
    desc: 'Arms a serf for guard duty and war — thrusts one hex out, good for holding a line. Armed serfs don’t labor.' },
  bow: { name: 'Bow', kind: 'weapon', skill: 'bow', atk: 5, range: 3, forgeCost: { wood: 18 }, forgeTime: 26,
    desc: 'Looses arrows three hexes out — fights from behind the line.' },
  axe: { name: 'Axe', kind: 'tool', skill: 'log', forgeCost: { wood: 12 }, forgeTime: 18,
    desc: 'Fells trees fast — for chop orders and clearing wooded building plots. The village’s wood supply starts here.' },
  pick: { name: 'Pick', kind: 'tool', skill: 'mine', forgeCost: { wood: 12 }, forgeTime: 20,
    desc: 'Breaks rock at quarries and mines — the tool for stone and ore.' },
  // -- stone tier (Workbench; the quarry feeds the toolmaker) --
  smithhammer: { name: "Smith's Hammer", kind: 'tool', skill: 'craft', forgeCost: { wood: 8, stone: 6 }, forgeTime: 20,
    desc: 'The master crafter’s hammer — required to run the Smithy and forge metal gear.' },
  shovel: { name: 'Shovel', kind: 'tool', skill: 'build', forgeCost: { wood: 8 }, forgeTime: 12,
    desc: 'Moves earth: dig and fill orders, and levelling the ground under new building sites.' },
  rod: { name: 'Fishing Rod', kind: 'tool', skill: 'fish', forgeCost: { wood: 12 }, forgeTime: 18,
    desc: 'Catches fish from the shore and piers — steady food without a farm.' },
  // -- metal tier (Smithy, crafter required) --
  sword: { name: 'Sword', kind: 'weapon', skill: 'sword', atk: 8, range: 0, forgeCost: { wood: 6, iron: 14 }, forgeTime: 30,
    desc: 'The strongest close-quarters weapon — cuts harder than a spear but must fight toe to toe. Armed serfs don’t labor.' },
}

/** New villages begin without tools or weapons. Raw building supplies remain. */
export const START_ITEMS: Partial<Record<ItemType, number>> = {}

/** The town-hall armory holds this many spare (unheld) items — a reserve arsenal. */
export const ARMORY_CAP = 15
/** Gear still craftable in the game (retired items cannot receive stock targets). */
export const ARMORY_ITEMS = (Object.keys(ITEMS) as ItemType[]).filter(item =>
  Object.values(BUILDINGS).some(def => def.forges?.includes(item)))

/** Ticks a serf spends at the armory depositing their old item and collecting the
 *  new one — a short, visible, interruptible beat even when they start adjacent. */
export const SWAP_TICKS = 2

// ---- NPC factions ---------------------------------------------------------------------

export interface NpcTierDef {
  name: string
  /** Rough building layout: counts by type placed around the NPC town hall. */
  layout: Partial<Record<BuildingType, number>>
  /** Garrison maintained (respawned slowly). */
  garrison: Partial<Record<UnitType, number>>
  /** Ticks between raids on nearby villages. 0 = never raids. */
  raidEvery: number
  /** Units sent per raid. */
  raidSquad: Partial<Record<UnitType, number>>
  /** Gold looted by whoever destroys its town hall. */
  bounty: number
}

export const NPC_TIERS: NpcTierDef[] = [
  {
    name: 'Hamlet',
    layout: { house: 2, farm: 2 },
    garrison: { spearman: 2 },
    raidEvery: 0,
    raidSquad: {},
    bounty: 20,
  },
  {
    name: 'Village',
    layout: { house: 3, farm: 3, tower: 1 },
    garrison: { spearman: 3, archer: 1 },
    raidEvery: 0,
    raidSquad: {},
    bounty: 50,
  },
  {
    name: 'Fortified Town',
    layout: { house: 4, farm: 3, tower: 2, barracks: 1 },
    garrison: { spearman: 4, archer: 3 },
    raidEvery: 480,
    raidSquad: { raider: 3 },
    bounty: 120,
  },
  {
    name: 'Warlord Keep',
    layout: { house: 5, farm: 4, tower: 3, barracks: 1 },
    garrison: { spearman: 6, archer: 4 },
    raidEvery: 360,
    raidSquad: { raider: 5, archer: 2 },
    bounty: 300,
  },
]

// Raider camps (WILD-owned 'camp' buildings)
export const CAMP_SPAWN_EVERY = 420 // ticks between raid waves from a camp
export const CAMP_SQUAD = 3 // raiders per wave
export const CAMP_BOUNTY = 80 // gold for destroying a camp
export const RAID_RANGE = 110 // camps/NPCs raid villages within this many hexes
/** World-population bounds: the map only keeps NPC life NEAR the living players.
 *  Without these, every visitor who ever founded a village left behind NPC
 *  hamlets and wildlife that ticked forever — prod reached 722 factions and
 *  19.6k units for 7 players, and the sim ate over half its tick budget. */
export const NPC_KEEP_RANGE = 200 // hexes from a player anchor an NPC village survives
export const WILDLIFE_RANGE = 96 // hexes from a player anchor wildlife lives/spawns
export const WILD_ANIMAL_MAX = 1500 // global backstop on wolves+deer+bears
export const NPC_PER_PLAYER = 12 // NPC-village count cap: base + per-player
export const NPC_BASE = 30

// Wildlife caps per chunk (worldgen seeds these; npc system keeps populations roughly stable)
export const WOLVES_PER_FOREST_CHUNK = 1
export const WOLF_FOREST_MIN = 48 // trees needed for wolf habitat, shared by generation and regrowth
export const WOLF_REGION_CHUNKS = 2 // budget spans a 2×2 chunk region, including roaming wolves
export const WOLVES_PER_REGION = 2
export const DEER_PER_GRASS_CHUNK = 2

// Misc
export const NAME_MAX = 24
export const CHAT_MAX = 240
export const MAX_SUBSCRIBED_CHUNKS = 1200 // must exceed the most chunks visible at min zoom
export const MAX_CHUNK_REQ = 80 // per 'chunks' message
/** A client may only generate/stream chunks within this many hexes of its home. */
export const MAX_GEN_DIST = 3000
/** Sweep cold, pristine chunks out of memory this often (ticks). */
export const CHUNK_GC_EVERY = 120
/** Keep generated chunks within this many hexes of any player holding; GC the rest. */
export const CHUNK_KEEP_DIST = 400
export const ATTACK_MOVE_ACQUIRE = 3 // attack-move target acquisition radius

/** Fog of war: a faction sees every hex within this distance of its territory
 *  or of any of its units. Enforced server-side (entities outside a player's
 *  vision are never sent to them) and mirrored by the client's shroud render. */
export const VISION_R = 10

/** Max units that may stand on one hex. Movement, spawning and group orders
 *  all respect this; the client renders each unit in its own slot so a full
 *  hex reads as distinct figures, not a pile. (Was 7 — five keeps hexes
 *  readable; overfull hexes from older saves simply drain, entry re-enforces.) */
export const HEX_CAP = 5

export const COLORS = [
  '#e6553a', '#3a8de6', '#43c56d', '#e0b73c', '#a75ce6',
  '#e66fb0', '#4ecbc4', '#e08b3c', '#8fd447', '#6f7de6',
]
export const NPC_COLOR = '#b0a48c'
export const WILD_COLOR = '#8a2f2f'
